import { type ChangeEvent, useCallback, useEffect, useId, useState } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useWhoami } from '@interxfi/api-client';
import { useIxmUserauthV1SessionServiceGetMfaSecret } from '@interxfi/api-client/generated/hooks/ixm-userauth-v1-session-service-get-mfa-secret';
import { useConfirmMFA } from '@interxfi/api-client/userauth/confirm-mfa';
import { HorizontalStack } from '@interxfi/design-system/components/alignment/horizontal-stack';
import { VerticalStack } from '@interxfi/design-system/components/alignment/vertical-stack';
import { ButtonLink } from '@interxfi/design-system/components/button/button-link';
import { SingleDigitInput } from '@interxfi/design-system/components/input/single-digit-input/single-digit-input';
import { TextLink } from '@interxfi/design-system/components/link/text-link/text-link';
import { useAddToast } from '@interxfi/design-system/components/toast/toast-context';
import { Typography } from '@interxfi/design-system/components/typography';
import { AuthLayout } from '../features/auth/layout';
import { MfaBackupCodeDetail } from '../features/userauth/mfa/mfa-backup-code-detail';
import { MfaSetupQrSection } from '../features/userauth/mfa/mfa-setup-qr-section';
import { getDefaultAuthenticatedRoute } from '../util/auth';
import { buildDefaultErrorMessageGetter, useErrorHandler } from '../util/error-handler';
import { DEFAULT_UNAUTHENTICATED_ROUTE } from '../util/routing/defaults';
import type { RouteGuardOptions } from '../util/routing/guard';
import { getPageHead } from '../util/routing/meta';

export const Route = createFileRoute('/mfa-setup')({
  staticData: {
    guard: { auth: { requireAuthenticated: true, redirectToIfNotMet: DEFAULT_UNAUTHENTICATED_ROUTE } },
  } as const satisfies RouteGuardOptions,
  head: () => getPageHead('MFA Setup'),
  component: MFASetup,
});

const mfaSecretErrorMessageGetter = buildDefaultErrorMessageGetter(
  'MFA configuration failed to load',
  'start the multi-factor authentication MFA setup flow',
);

const confirmMfaErrorMessageGetter = buildDefaultErrorMessageGetter('MFA configuration failed to save', 'set up multi-factor authentication (MFA)');

function MFASetup() {
  const { data: whoamiData } = useWhoami();
  const navigateTo = getDefaultAuthenticatedRoute(whoamiData);
  const id = useId();
  const addToast = useAddToast();
  const [otp, setOtp] = useState('');
  const { data, error: mfaSecretError, mutate } = useIxmUserauthV1SessionServiceGetMfaSecret();
  useErrorHandler(mfaSecretError, mfaSecretErrorMessageGetter);

  const otpFieldDescriptionId = `${id}-otpFieldDescription`;

  const { data: confirmedData, mutateAsync, isPending: isSubmittingMFAConfirmation, error: submitMFAConfirmationError } = useConfirmMFA();
  useErrorHandler(submitMFAConfirmationError, confirmMfaErrorMessageGetter);

  useEffect(() => {
    mutate(undefined);
  }, [mutate]);

  const handleOtpChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setOtp(e.target.value);
  }, []);

  const onFillOtp = useCallback(
    async (finalOtp: string) => {
      try {
        await mutateAsync({ otp: finalOtp, secret: data?.secret });

        addToast({
          id: 'addMFASuccess',
          content: 'Two-Step Authentication has been activated for your account.',
          variant: 'success',
        });
      } catch {
        setOtp('');
      }
    },
    [addToast, data?.secret, mutateAsync],
  );

  return (
    <AuthLayout>
      {confirmedData?.recoveryCode ? (
        <>
          <Typography as="h1" lineHeight="1.5rem" size="1.5rem" variant="heading">
            Backup Code
          </Typography>

          <MfaBackupCodeDetail backupCode={confirmedData.recoveryCode} />

          <ButtonLink fullWidth LinkElement={Link} size="large" to={navigateTo} variant="primary">
            OK, Got It
          </ButtonLink>
        </>
      ) : (
        <>
          <Typography as="h1" lineHeight="1.5rem" size="1.5rem" variant="heading">
            Two-Step Authentication
          </Typography>

          <VerticalStack gap={4}>
            <Typography lineHeight="1.125rem" size="0.875rem">
              Interchange requires the use of an authenticator app, in addition to your password, to ensure secure account access.
            </Typography>

            <MfaSetupQrSection secret={data?.secret} url={data?.url} />

            <VerticalStack gap={2}>
              <Typography id={otpFieldDescriptionId} lineHeight="1.125rem" size="0.875rem">
                6-digit authentication code:
              </Typography>

              <SingleDigitInput
                aria-describedby={otpFieldDescriptionId}
                autoFocus
                disabled={isSubmittingMFAConfirmation}
                hideLabel
                label="One-Time Password (OTP)"
                length={6}
                name="otp"
                onChange={handleOtpChange}
                onFill={onFillOtp}
                value={otp}
              />
            </VerticalStack>
          </VerticalStack>
        </>
      )}

      <HorizontalStack align="center" gap={2} wrap="nowrap">
        <VerticalStack inlineAlign="center">
          <TextLink LinkElement={Link} size="medium" to="/logout" underline="hover">
            Sign Out
          </TextLink>
        </VerticalStack>
      </HorizontalStack>
    </AuthLayout>
  );
}
